import { lazy, Suspense, useMemo, useState } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import {
  replaceRoutesByPath,
  RenderFailureRouterTap,
  AppConfigProvider,
  AuthProvider,
  AuthGate,
  ApiProvider,
  createMockAuth,
  RouteFallback,
  useIdentityGap,
  useConsentGap,
  composeApiErrorHandlers,
  IdentityGapGate,
  RegistrationGate,
  createIdentitySuppression,
  ThemeProvider,
  browserSystemSchemeDetector,
} from '@agnivo/app-core';
// The OIDC provider is NOT in the app-core barrel — it is behind its own subpath so that a
// shell which does not import it cannot carry `oidc-client-ts`, whatever `authMode` turns out
// to be (architect ruling 2026-08-02; rationale in packages/app-core/src/auth/oidc.ts).
import {
  useOidcAuth,
  OidcCallbackScreen,
  OIDC_CALLBACK_PATH,
} from '@agnivo/app-core/auth/oidc';
import { appConfig, authMode, oidcConfig } from './config';
import { ConsentGate } from './routes/account/ConsentGate';
import { sectionRoutes, widgetRoutesFor } from './routes/admin-tenant/routes';
import { accountRoutes } from './routes/account/routes';
import { conversationRoutes } from './routes/conversations/routes';

const REGISTER_PATH = '/register';
// Lazy: only a registrant ever loads it, so it stays out of the entry chunk.
const RegisterRoute = lazy(() => import('./routes/account/RegisterPage').then((m) => ({ default: m.RegisterRoute })));

/**
 * web-ui = the reference THIN SHELL over @agnivo/app-core (ADR-079). It owns:
 *  - the router factory (createBrowserRouter — history routing; do NOT
 *    hash-route the web surface),
 *  - its runtime config (from its own Vite env),
 *  - its auth provider choice (mock | Zitadel OIDC PKCE).
 */

// Coherence guard the shell owns (app-core can't tell a mock provider apart):
// a live API with mock auth sends unauthenticated requests → 401 storms.
if (appConfig.apiMode === 'live' && authMode === 'mock') {
  throw new Error('VITE_API_MODE=live requires VITE_AUTH_MODE=oidc (mock auth sends no bearer)');
}

/**
 * The admin-tenant SECTION owns '/agents' on THIS surface (Dmitry DM 452; architect
 * Option A + staging 2026-08-18T16:08Z): its lazy routes replace the chat-side list
 * child, while chat-side '/agents/new' (create) stays mounted until the section's own
 * create flow lands — '/agents/new' must resolve at every moment between the two
 * phases (the §1d pin; asserted in test/adminSectionComposition.test.tsx).
 * The SHARED table is never mutated: mini-app / desktop / mobile keep the chat-side
 * '/agents' flows (replaceRoutesByPath builds a new table; web!406).
 */
// ADR-156 widget-config page: the route exists ONLY where `widgetConfigEnabled` is on (dev image); with
// it off the path is simply unmatched, never a dead-end page. Read once at module scope, like the table.
// (The route and its lazy chunk are still BUNDLED when off — the flag is a runtime read — they are just
// never requested.) One-line call shape on purpose: `test/accountRoutePin.test.ts` source-pins it.
const widgetTableRoutes = widgetRoutesFor(appConfig.widgetConfigEnabled);
const router = createBrowserRouter(replaceRoutesByPath(['/agents'], [...sectionRoutes, ...accountRoutes, ...conversationRoutes, ...widgetTableRoutes]));

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <AppConfigProvider config={appConfig}>
      {/* ThemeProvider sits HERE, inside Shell, so it also covers the OIDC
          callback branch below — that branch returns a Shell WITHOUT the
          router, and a provider mounted around <RouterProvider> would miss it.
          Required, not optional: SettingsScreen calls useTheme(), which throws
          without a provider, so skipping this serves a crashed /settings rather
          than an unthemed one. */}
      <ThemeProvider detector={browserSystemSchemeDetector}>
        {children}
      </ThemeProvider>
    </AppConfigProvider>
  );
}

function AppBody() {
  // A valid OIDC session with no platform `users` row fails EVERY call
  // (IDENTITY_NOT_PROVISIONED). Latch it off any live call and gate the surface
  // rather than let the generic handler re-authenticate a token that is already
  // valid — that loop can never resolve itself. `gap_web` is this surface's
  // registered attribution param (mc!151); the union type pins it.
  const gap = useIdentityGap();
  // ADR-182: the consent re-ask latches off the server's CONSENT_REQUIRED / PROFILE_INCOMPLETE refusal.
  // `ApiProvider` takes ONE handler, hence the composition (identity gap first; both always run).
  const consent = useConsentGap();
  // ADR-182 D1.3: while the registration bootstrap probes, IDENTITY_NOT_PROVISIONED is the EXPECTED answer for a
  // fresh registrant and must not latch the (sticky) gap; `resolve()` ends the suppression on every bootstrap exit.
  const [suppression] = useState(createIdentitySuppression);
  const onApiError = useMemo(
    () => composeApiErrorHandlers(suppression.wrap(gap.onApiError), consent.onApiError),
    [suppression, gap.onApiError, consent.onApiError],
  );
  return (
    <ApiProvider onApiError={onApiError}>
      {/* No ShellNavItemsProvider: this shell registers NO extra nav items. It
          used to add «Мои ассистенты» → /agents, which app-core's built-in
          «Агенты» item already points at — two entries, one screen. The slot
          mechanism stays in app-core for a shell that has something genuinely
          its own to add. */}
      <RegistrationGate onResolved={suppression.resolve}>
      <IdentityGapGate gap={gap} surface="gap_web">
       <ConsentGate gap={consent}>
        {/* lazy/loader failures never mount any route element — the router
            catches them into its own state, so the element-level boundary in
            the shared route table cannot see them. This tap reads the router
            object and reports them; it defines no errorElement, so the
            router's default error UI stays exactly what it was. */}
        <RenderFailureRouterTap router={router} />
        {/* `fallbackElement={<RouteFallback />}` covers the initial-mount blank
            during routes.tsx's per-route lazy resolution (web!598 product review).
            Without it, react-router v6.28 renders `null` while `router.state.initialized`
            is false and the user sees an empty container until the first chunk lands. */}
        <RouterProvider router={router} fallbackElement={<RouteFallback />} />
       </ConsentGate>
      </IdentityGapGate>
      </RegistrationGate>
    </ApiProvider>
  );
}

function OidcApp() {
  const auth = useOidcAuth(oidcConfig);
  return (
    <AuthProvider auth={auth}>
      <AuthGate>
        <AppBody />
      </AuthGate>
    </AuthProvider>
  );
}

// /register mounts OUTSIDE `AuthGate` on purpose (ADR-182 D1; architect 2026-09-30): the registrant has no
// session, and `accounts.register` is the one unauthenticated route. Providers stay; only the gate is skipped.
function OidcRegister() {
  const auth = useOidcAuth(oidcConfig);
  return (
    <AuthProvider auth={auth}>
      <ApiProvider>
        <Suspense fallback={<RouteFallback />}>
          <RegisterRoute />
        </Suspense>
      </ApiProvider>
    </AuthProvider>
  );
}

function MockRegister() {
  return (
    <AuthProvider auth={createMockAuth()}>
      <ApiProvider>
        <Suspense fallback={<RouteFallback />}>
          <RegisterRoute />
        </Suspense>
      </ApiProvider>
    </AuthProvider>
  );
}

function MockApp() {
  return (
    <AuthProvider auth={createMockAuth()}>
      <AppBody />
    </AuthProvider>
  );
}

export function App() {
  // The callback route completes the code exchange UNAUTHENTICATED and exits
  // via hard navigation (location.replace) — it never mounts the router or the
  // gate. Plain pathname check is stable for the lifetime of this page load.
  if (authMode === 'oidc' && window.location.pathname === OIDC_CALLBACK_PATH) {
    return (
      <Shell>
        <OidcCallbackScreen config={oidcConfig} />
      </Shell>
    );
  }
  if (window.location.pathname === REGISTER_PATH) {
    return <Shell>{authMode === 'oidc' ? <OidcRegister /> : <MockRegister />}</Shell>;
  }
  return <Shell>{authMode === 'oidc' ? <OidcApp /> : <MockApp />}</Shell>;
}
